import { SHELL_SECTION, shellContexts, shellSteps } from "../components/AppLayout/help.shell";
import { benefitsHelp } from "../pages/BenefitsPage/help";
import { dashboardHelp } from "../pages/Dashboard/help";
import { employerHelp } from "../pages/EmployerPage/help";
import { homeHelp } from "../pages/HomePage/help";
import { profileHelp } from "../pages/ProfilePage/help";
import type { PageHelp } from "./types";

/**
 * Route → page guide, WITHOUT the app shell. A route that is not listed here simply has no "?"
 * button, which is what lets the manual roll out page by page.
 *
 * Literal paths rather than `ROUTES.*`: this file is imported by Node scripts (the manual
 * generator) that do not resolve the app's `baseUrl` imports. `registry.test.ts` proves every
 * route in `App.tsx` resolves here, so the two cannot drift apart unnoticed.
 *
 * A key may carry `:param` segments — a detail page is a different page with its own guide.
 *
 * Keep this file free of React/UI imports.
 */
export const PAGE_GUIDES: Record<string, PageHelp> = {
  "/": homeHelp,
  "/benefits": benefitsHelp,
  "/profile": profileHelp,
  "/employer": employerHelp,
  "/dashboard": dashboardHelp,
};

/**
 * The controls on every page (navigation, header, assistant) as a guide of their own — the
 * printable manual renders it once, as its first chapter.
 */
export const SHELL_GUIDE: PageHelp = {
  title: "Around the app",
  intro:
    "The navigation, the header icons and the AI assistant are the same on every page. This " +
    "chapter covers them once; in the app, each page's guide lists them at the end.",
  sections: [SHELL_SECTION],
  contexts: shellContexts,
  steps: shellSteps,
};

/**
 * A page guide with the shell steps appended, so the tour, the search and "point at a field"
 * also answer for the navigation and the header. Shell last: on a page, the page comes first.
 */
const withShell = (page: PageHelp): PageHelp => ({
  ...page,
  sections: [...(page.sections ?? []), SHELL_SECTION],
  contexts: { ...page.contexts, ...shellContexts },
  steps: [...page.steps, ...shellSteps],
});

/** Route → the guide the app shows: the page's own steps, then the shell's. */
export const HELP_REGISTRY: Record<string, PageHelp> = Object.fromEntries(
  Object.entries(PAGE_GUIDES).map(([route, page]) => [route, withShell(page)])
);

const isPattern = (route: string): boolean => route.includes("/:");

/** Segment-wise match, where `:param` accepts any single non-empty segment. */
export const matchesRoute = (route: string, pathname: string): boolean => {
  const pattern = route.split("/");
  const actual = pathname.split("/");
  if (pattern.length !== actual.length) return false;
  return pattern.every((segment, i) =>
    segment.startsWith(":") ? (actual[i] ?? "").length > 0 : segment === actual[i]
  );
};

export const helpForPath = (pathname: string): PageHelp | null => {
  const exact = HELP_REGISTRY[pathname];
  if (exact) return exact;

  // Patterns before prefixes: a detail route belongs to its own guide, not to the list guide it
  // happens to sit under. Fewest params wins, so a literal-heavy route is preferred.
  const pattern = Object.keys(HELP_REGISTRY)
    .filter((route) => isPattern(route) && matchesRoute(route, pathname))
    .sort((a, b) => a.split("/:").length - b.split("/:").length)[0];
  if (pattern) return HELP_REGISTRY[pattern] ?? null;

  // Prefix match so a sub-route with no guide of its own still gets its parent's guide. Longest
  // match wins. "/" is excluded: the home guide must not answer for every unknown route.
  const hit = Object.keys(HELP_REGISTRY)
    .filter((route) => route !== "/" && !isPattern(route) && pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? (HELP_REGISTRY[hit] ?? null) : null;
};
